import { HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { take } from 'rxjs';
import {
  DashboardFlightDuration,
  DashboardFlightListItem,
  DashboardFlightsBrief,
  DashboardReportErrors,
  DashboardServiceDoneItem,
  DashboardServiceDuePerPartItem,
  DashboardServiceDuePerDroneItem,
  ReportRequest,
} from '../models/report.model';
import {
  DashboardDataBuilderOptions,
  DashboardDataBuilderService,
} from './dashboard-data-builder.service';

const INITIAL_BRIEF: DashboardFlightsBrief = {
  totalFlightsCount: 0,
  totalFlightSeconds: 0,
  avgFleetFlightSeconds: 0,
};

const INITIAL_REPORT_ERRORS: DashboardReportErrors = {
  brief: null,
  highestFlightDurations: null,
  lowestFlightDurations: null,
  serviceDuePerDrone: null,
  serviceDuePerPart: null,
  serviceDone: null,
};

@Injectable({
  providedIn: 'root',
})
export class DashboardDataStore {
  private readonly listLimit = 10;

  private readonly loadingState = signal(false);
  private readonly loadErrorState = signal<unknown>(null);
  private readonly briefState = signal<DashboardFlightsBrief>(INITIAL_BRIEF);
  private readonly highestFlightItemsState = signal<DashboardFlightListItem[]>(
    [],
  );
  private readonly lowestFlightItemsState = signal<DashboardFlightListItem[]>(
    [],
  );
  private readonly serviceDuePerDroneItemsState = signal<
    DashboardServiceDuePerDroneItem[]
  >([]);
  private readonly serviceDuePerPartItemsState = signal<
    DashboardServiceDuePerPartItem[]
  >([]);
  private readonly serviceDoneItemsState = signal<DashboardServiceDoneItem[]>(
    [],
  );
  private readonly reportErrorsState = signal<DashboardReportErrors>(
    INITIAL_REPORT_ERRORS,
  );

  readonly loading = this.loadingState.asReadonly();
  readonly loadError = this.loadErrorState.asReadonly();
  readonly brief = this.briefState.asReadonly();
  readonly highestFlightItems = this.highestFlightItemsState.asReadonly();
  readonly lowestFlightItems = this.lowestFlightItemsState.asReadonly();
  readonly serviceDuePerDroneItems =
    this.serviceDuePerDroneItemsState.asReadonly();
  readonly serviceDuePerPartItems =
    this.serviceDuePerPartItemsState.asReadonly();
  readonly serviceDoneItems = this.serviceDoneItemsState.asReadonly();
  readonly reportErrors = this.reportErrorsState.asReadonly();
  readonly briefLoadError = computed(() => this.reportErrorsState().brief);
  readonly highestFlightsLoadError = computed(
    () => this.reportErrorsState().highestFlightDurations,
  );
  readonly lowestFlightsLoadError = computed(
    () => this.reportErrorsState().lowestFlightDurations,
  );
  readonly serviceDuePerDroneLoadError = computed(
    () => this.reportErrorsState().serviceDuePerDrone,
  );
  readonly serviceDuePerPartLoadError = computed(
    () => this.reportErrorsState().serviceDuePerPart,
  );
  readonly serviceDoneLoadError = computed(
    () => this.reportErrorsState().serviceDone,
  );
  readonly hasPartialFailures = computed(() =>
    Object.values(this.reportErrorsState()).some((error) => error !== null),
  );
  readonly isUnauthorized = computed(
    () => this.getHttpStatus(this.loadErrorState()) === 401,
  );
  /** The backend serves report queries to administrators and maintainers only. */
  readonly isForbidden = computed(
    () => this.getHttpStatus(this.loadErrorState()) === 403,
  );
  readonly errorStatusLabel = computed(() => {
    const error = this.loadErrorState();
    const status = this.getHttpStatus(error);
    if (status === null) {
      return null;
    }

    const statusText =
      this.getHttpStatusText(error) ??
      (status === 0 ? 'Network error' : 'Request failed');
    return `${status} ${statusText}`;
  });

  readonly errorDetail = computed(() => {
    const error = this.loadErrorState();
    if (!error) {
      return null;
    }

    if (error instanceof HttpErrorResponse) {
      return error.message;
    }

    if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
    ) {
      return (error as { message: string }).message;
    }

    return null;
  });

  readonly errorMessage = computed(() => {
    const error = this.loadErrorState();
    if (!error) {
      return null;
    }

    if (this.isUnauthorized()) {
      return 'dashboard.messages.unauthorized';
    }

    if (this.isForbidden()) {
      return 'dashboard.messages.forbidden';
    }

    return 'dashboard.messages.failed_load';
  });

  readonly totalFlightHoursValue = computed(
    () => this.briefState().totalFlightSeconds / 3600,
  );
  readonly totalFlightHours = computed(() =>
    this.totalFlightHoursValue().toFixed(2),
  );
  readonly avgFleetFlightHoursValue = computed(
    () => this.briefState().avgFleetFlightSeconds / 3600,
  );
  readonly totalFlightHoursSubtitle = computed(
    () => `${this.totalFlightHours()} hours`,
  );

  constructor(
    private readonly dashboardDataBuilderService: DashboardDataBuilderService,
  ) {}

  loadDashboardData(
    body: ReportRequest = {},
    options: DashboardDataBuilderOptions = {},
  ): void {
    const includeServiceReports = options.includeServiceReports ?? true;
    const previousReportErrors = this.reportErrorsState();
    this.loadingState.set(true);
    this.loadErrorState.set(null);
    this.reportErrorsState.set(
      includeServiceReports
        ? INITIAL_REPORT_ERRORS
        : {
            ...previousReportErrors,
            brief: null,
            highestFlightDurations: null,
            lowestFlightDurations: null,
          },
    );

    const dashboardData$ = includeServiceReports
      ? this.dashboardDataBuilderService.getFlightsDashboardData(body)
      : this.dashboardDataBuilderService.getFlightsDashboardData(body, {
          includeServiceReports: false,
        });

    dashboardData$.pipe(take(1)).subscribe({
      next: (dashboardData) => {
        const mergedErrors: DashboardReportErrors = includeServiceReports
          ? dashboardData.errors
          : {
              brief: dashboardData.errors.brief,
              highestFlightDurations:
                dashboardData.errors.highestFlightDurations,
              lowestFlightDurations: dashboardData.errors.lowestFlightDurations,
              serviceDuePerDrone: previousReportErrors.serviceDuePerDrone,
              serviceDuePerPart: previousReportErrors.serviceDuePerPart,
              serviceDone: previousReportErrors.serviceDone,
            };
        const fatalError = this.getFatalError(mergedErrors);
        this.briefState.set(dashboardData.brief);
        this.highestFlightItemsState.set(
          this.mapListItems(dashboardData.flightDurations).slice(
            0,
            this.listLimit,
          ),
        );
        this.lowestFlightItemsState.set(
          this.mapListItems(dashboardData.lowestFlightDurations).slice(
            0,
            this.listLimit,
          ),
        );
        if (includeServiceReports) {
          this.serviceDuePerDroneItemsState.set(
            dashboardData.serviceDuePerDroneItems.slice(0, this.listLimit),
          );
          this.serviceDuePerPartItemsState.set(
            dashboardData.serviceDuePerPartItems.slice(0, this.listLimit),
          );
          this.serviceDoneItemsState.set(
            dashboardData.serviceDoneItems.slice(0, this.listLimit),
          );
        }
        this.reportErrorsState.set(mergedErrors);
        this.loadErrorState.set(fatalError);
        this.loadingState.set(false);
      },
      error: (error) => {
        this.loadErrorState.set(error);
        this.loadingState.set(false);
      },
    });
  }

  private mapListItems(
    durations: DashboardFlightDuration[],
  ): DashboardFlightListItem[] {
    return durations.map((duration) => ({
      uav: duration.uav,
      flightTime: duration.flightTime,
      flightsCount: duration.flightsCount,
    }));
  }

  private getFatalError(errors: DashboardReportErrors): unknown | null {
    const allReportsFailed = Object.values(errors).every(
      (error) => error !== null,
    );

    if (!allReportsFailed) {
      return null;
    }

    return Object.values(errors).find((error) => error !== null) ?? null;
  }

  private getHttpStatus(error: unknown): number | null {
    if (error instanceof HttpErrorResponse) {
      return Number.isFinite(error.status) ? error.status : null;
    }

    if (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      typeof (error as { status?: unknown }).status === 'number'
    ) {
      return (error as { status: number }).status;
    }

    return null;
  }

  private getHttpStatusText(error: unknown): string | null {
    if (error instanceof HttpErrorResponse) {
      return error.statusText || null;
    }

    if (
      typeof error === 'object' &&
      error !== null &&
      'statusText' in error &&
      typeof (error as { statusText?: unknown }).statusText === 'string'
    ) {
      return (error as { statusText: string }).statusText;
    }

    return null;
  }
}
